import { Grid, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../app/store/configureStore';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { resetPasswordValidationSchema } from './resetPasswordValidation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from './accountSlice';
import { toast } from 'react-toastify';
import FormWrapper from '../../app/components/FormWrapper';
import FormButtons from '../../app/components/FormButtons';
import { ResetPasswordFormData } from '../../app/data/models/accounts/resetPasswordFormData';

interface Props {
  cancelEdit?: () => void;
}

const ResetPasswordForm = ({ cancelEdit }: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { control, handleSubmit } = useForm<ResetPasswordFormData>({
    mode: 'onTouched',
    resolver: yupResolver<ResetPasswordFormData>(
      resetPasswordValidationSchema(t)
    ),
  });

  const handleSubmitData = async (data: ResetPasswordFormData) => {
    try {
      if (!token || !email) {
        toast.error(t('account.reset-password.invalid-request'));
        return;
      }

      const isPasswordResetSuccessful = await dispatch(
        resetPassword({ token, email, password: data.password })
      ).unwrap();

      if (isPasswordResetSuccessful) {
        toast.success(t('account.reset-password.success'));
        navigate('/account/login');
      }
    } catch (error) {
      toast.error(t('account.reset-password.error'));
      console.error('Reset password error:', error);
    }
  };

  const handleCancel = () => {
    if (cancelEdit) {
      cancelEdit();
    } else {
      navigate('/account/login');
    }
  };

  return (
    <FormWrapper title={t('account.reset-password.title')}>
      <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="password"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="password"
                  label={t('account.reset-password.password')}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="password"
                  label={t('account.reset-password.confirm-password')}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        <FormButtons
          cancelEdit={handleCancel}
          loading={false}
          cancelText={t('common.cancel')}
          submitText={t('common.submit')}
        />
      </form>
    </FormWrapper>
  );
};

export default ResetPasswordForm;
